/**
 * #4506 — one definition of "the same game link", shared by every path that adds a Web Game: the public form,
 * the review queue's approve, the admin create/edit and bulk import. Stored in `url_key` (unique) so the database
 * settles a race the code cannot.
 *
 * Two links share a key when they differ only in things that never change which page loads:
 * scheme (http/https), the host's letter case, a leading `www.`, a default port, a `#fragment`, trailing slashes or `/index.html`,
 * tracking parameters (utm_*, fbclid, gclid ...) and the ORDER of the remaining query parameters. A different path,
 * a different letter case in the path or a query value, or a different remaining query parameter is a different game:
 * `site.com/?game=1` is not `site.com/?game=2`, and `site.com/Play` is not `site.com/play`.
 */

const TRACKING_PARAMS = new Set(['fbclid', 'gclid', 'dclid', 'msclkid', 'igshid', 'mc_cid', 'mc_eid', '_ga']);
const isTrackingParam = (name: string) => name.startsWith('utm_') || TRACKING_PARAMS.has(name);

export function canonicalGameKey(url: string): string {
    const raw = url.trim();
    let parsed: URL;
    try {
        parsed = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(raw) ? raw : `https://${raw}`);
    } catch {
        return raw.replace(/\/+$/, '');
    }
    // Only the host is case-insensitive. A path or a query value can be case-sensitive (`?room=aB3x`), so two links that
    // differ only there are two games rather than one wrongly refused.
    const host = parsed.hostname.toLowerCase().replace(/^www\./, '').replace(/\.$/, '');
    // A non-default port is part of the address. `URL` already drops the default one for the scheme.
    const port = parsed.port && !(parsed.protocol === 'http:' && parsed.port === '80') && !(parsed.protocol === 'https:' && parsed.port === '443')
        ? `:${parsed.port}` : '';
    const path = parsed.pathname.replace(/\/{2,}/g, '/').replace(/\/index\.html?$/i, '/').replace(/\/+$/, '');
    // Raw tokens, not decoded values: `a=1%26b=2` is one parameter and must not read as two.
    const params = parsed.search.slice(1).split('&').filter(Boolean)
        .filter(token => !isTrackingParam(safeDecode(token.split('=')[0]).toLowerCase()))
        .sort();
    return `${host}${port}${path}${params.length ? `?${params.join('&')}` : ''}`;
}

function safeDecode(value: string): string {
    try { return decodeURIComponent(value); } catch { return value; }
}
